import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useImpersonation } from '@/hooks/useImpersonation';
import { useTeamMembersWithRoles } from '@/hooks/useTeamMembersWithRoles';
import { toast } from 'sonner';
import { Loader2, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AppRole } from '@/hooks/useUserRole';

interface AdminImpersonationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ROLES = [
  { value: 'admin', label: 'Admin', color: 'bg-purple-500', description: 'Full access to all features' },
  { value: 'creative_manager', label: 'Creative Manager', color: 'bg-violet-500', description: 'Access to App Database and Task Manager only' },
  { value: 'procurement_manager', label: 'Procurement Manager', color: 'bg-teal-500', description: 'Access to operational and procurement pages' },
  { value: 'provider_manager', label: 'Provider Manager', color: 'bg-orange-500', description: 'Access to assigned tasks and quotations' },
  { value: 'qa_manager', label: 'QA Manager', color: 'bg-green-500', description: 'Access to App Database and Task Manager' },
  { value: 'ma_manager', label: 'M&A Manager', color: 'bg-cyan-500', description: 'Access to App Database and performance summaries' },
  { value: 'tech_apps', label: 'Tech Apps Manager', color: 'bg-emerald-500', description: 'Access to App Database and Task Manager' },
  { value: 'tech_infrastructure', label: 'Tech Infrastructure Manager', color: 'bg-slate-500', description: 'Access to App Database and Task Manager' }
];

const DEPARTMENT_MANAGER_ROLES = [
  { value: 'aso', label: 'ASO Manager', role: 'department_manager', department: 'aso', color: 'bg-blue-500', description: 'Store Optimization lab access' },
  { value: 'product', label: 'Product Manager', role: 'department_manager', department: 'product', color: 'bg-blue-500', description: 'Usability + Product labs access' },
  { value: 'compliance', label: 'Compliance Manager', role: 'department_manager', department: 'compliance', color: 'bg-blue-500', description: 'Compliance lab access' },
  { value: 'ad_monetization', label: 'Ad Monetization Manager', role: 'department_manager', department: 'ad_monetization', color: 'bg-blue-500', description: 'Ad Monetization lab access' },
  { value: 'user_acquisition', label: 'User Acquisition Manager', role: 'department_manager', department: 'user_acquisition', color: 'bg-blue-500', description: 'User Acquisition lab access' }
];

export function AdminImpersonationModal({ open, onOpenChange }: AdminImpersonationModalProps) {
  const { startImpersonation } = useImpersonation();
  const { teamMembers, loading: loadingMembers } = useTeamMembersWithRoles();
  
  const [mode, setMode] = useState<'user' | 'role'>('role');
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [duration, setDuration] = useState<string>('30');
  const [reason, setReason] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const getRoleColor = (role: AppRole | null): string => {
    if (!role) return 'bg-gray-500';
    const roleColors: Record<AppRole, string> = {
      'admin': 'bg-purple-500',
      'department_manager': 'bg-blue-500',
      'creative_manager': 'bg-violet-500',
      'procurement_manager': 'bg-teal-500',
      'provider_manager': 'bg-orange-500',
      'qa_manager': 'bg-green-500',
      'ma_manager': 'bg-cyan-500',
      'tech_apps': 'bg-emerald-500',
      'tech_infrastructure': 'bg-slate-500'
    };
    return roleColors[role] || 'bg-gray-500';
  };

  const getRoleLabel = (role: AppRole | null, department: string | null): string => {
    if (!role) return 'No Role';
    if (role === 'department_manager' && department) {
      const deptLabels: Record<string, string> = {
        'aso': 'ASO Manager',
        'product': 'Product Manager',
        'compliance': 'Compliance Manager',
        'ad_monetization': 'Ad Monetization Manager',
        'user_acquisition': 'User Acquisition Manager'
      };
      return deptLabels[department] || 'Department Manager';
    }
    const roleLabels: Record<AppRole, string> = {
      'admin': 'Admin',
      'department_manager': 'Department Manager',
      'creative_manager': 'Creative Manager',
      'procurement_manager': 'Procurement Manager',
      'provider_manager': 'Provider Manager',
      'qa_manager': 'QA Manager',
      'ma_manager': 'M&A Manager',
      'tech_apps': 'Tech Apps Manager',
      'tech_infrastructure': 'Tech Infrastructure Manager'
    };
    return roleLabels[role] || role;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      let params: any = {
        reason: reason || 'Admin debugging',
        durationMinutes: parseInt(duration) || 30
      };

      if (mode === 'user') {
        params.impersonatedUserId = selectedUserId;
      } else {
        // Check if it's a department manager role
        const deptManagerRole = DEPARTMENT_MANAGER_ROLES.find(r => r.value === selectedRole);
        
        if (deptManagerRole) {
          params.impersonatedRole = deptManagerRole.role;
          params.impersonatedDepartment = deptManagerRole.department;
        } else {
          params.impersonatedRole = selectedRole;
        }
      }

      // Add timeout wrapper (10 seconds)
      const timeoutPromise = new Promise<{ success: boolean; error: string }>((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout after 10 seconds')), 10000)
      );

      const result = await Promise.race([
        startImpersonation(params),
        timeoutPromise
      ]);

      if (result.success) {
        toast.success('Impersonation started successfully');
        onOpenChange(false);
        // Refresh page to apply impersonation
        setTimeout(() => window.location.reload(), 500);
      } else {
        toast.error('Failed to start impersonation');
      }
    } catch (error) {
      console.error('Error starting impersonation:', error);
      const errorMessage = error instanceof Error ? error.message : 'An error occurred';
      
      if (errorMessage.includes('timeout')) {
        toast.error('Request timeout. Please try again or check backend performance.');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-600" />
            Admin Impersonation
          </DialogTitle>
          <DialogDescription>
            View the application as another user or role. All actions are logged for security.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Mode Selection */}
          <div className="space-y-2">
            <Label>Impersonation Mode</Label>
            <Select value={mode} onValueChange={(val) => setMode(val as 'user' | 'role')}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="role">By Role/Department</SelectItem>
                <SelectItem value="user">By Specific User</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {mode === 'role' ? (
            <div className="space-y-2">
              <Label htmlFor="role">Role *</Label>
              <Select value={selectedRole} onValueChange={setSelectedRole} required>
                <SelectTrigger id="role">
                  <SelectValue placeholder="Select role..." />
                </SelectTrigger>
                <SelectContent className="max-h-[300px] overflow-y-auto" style={{ width: 'var(--radix-select-trigger-width)' }}>
                  {DEPARTMENT_MANAGER_ROLES.map(role => (
                    <SelectItem key={role.value} value={role.value}>
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${role.color}`}></div>
                          <span className="font-medium">{role.label}</span>
                        </div>
                        <span className="text-xs text-muted-foreground ml-4">{role.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                  {ROLES.map(role => (
                    <SelectItem key={role.value} value={role.value}>
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${role.color}`}></div>
                          <span className="font-medium">{role.label}</span>
                        </div>
                        <span className="text-xs text-muted-foreground ml-4">{role.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="user">User *</Label>
              <Select 
                value={selectedUserId} 
                onValueChange={setSelectedUserId} 
                required
                disabled={loadingMembers}
              >
                <SelectTrigger id="user">
                  <SelectValue placeholder="Select user..." />
                </SelectTrigger>
                <SelectContent className="max-h-[300px] overflow-y-auto" style={{ width: 'var(--radix-select-trigger-width)' }}>
                  {teamMembers.map(member => (
                    <SelectItem key={member.id} value={member.id}>
                      <div className="flex items-center gap-3 w-full">
                        <Avatar className="h-6 w-6">
                          <AvatarImage src={member.avatar_url || undefined} />
                          <AvatarFallback className={cn("text-white text-xs", getRoleColor(member.role))}>
                            {member.full_name?.[0] || member.email[0].toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex items-center gap-2 flex-1">
                          <div className={cn("w-2 h-2 rounded-full flex-shrink-0", getRoleColor(member.role))}></div>
                          <div className="flex flex-col gap-0.5 min-w-0">
                            <span className="text-sm font-medium truncate">{member.full_name || member.email}</span>
                            <span className="text-xs text-muted-foreground truncate">{getRoleLabel(member.role, member.department)}</span>
                          </div>
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="duration">Duration (minutes) *</Label>
            <Input
              id="duration"
              type="number"
              min="5"
              max="120"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              required
            />
            <p className="text-xs text-muted-foreground">
              Session will automatically expire after this time
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Reason</Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why are you impersonating? (logged for audit)"
              rows={3}
            />
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-900">
            <p className="font-semibold mb-1">⚠️ Security Notice</p>
            <p>This action is logged for security auditing. Only use for legitimate debugging purposes.</p>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Start Impersonation
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
